/**
 * Suggestion engines for the Rooms matrix.
 *
 * Pure functions that turn an upstream data shape (proposal_data,
 * service_log_items) into the matrix row shape used by the grid. The
 * UI layer feeds the output into local state, the operator reviews +
 * edits, then save commits to hosp_rooms.
 *
 * Two sources today, proposal and service log. The Rooms tab prefers
 * proposal when both exist (the proposal carries explicit room shape);
 * service log is the fallback for properties that signed up direct
 * with no proposal but have an invoice / log on file.
 */

export interface SuggestedRow {
  room_number: string;
  room_name: string;
  items: Record<string, number>;
}

interface ProposalRoom {
  name?: string;
  quantity?: number;
  items?: Array<{
    item_name?: string;
    quantity?: number;
  }>;
}

interface ProposalScope {
  rooms?: ProposalRoom[];
  items?: ServiceLogItem[];
}

interface ProposalData {
  scope?: ProposalScope;
}

/**
 * Build matrix rows from a proposal's proposal_data.scope.rooms.
 *
 * Proposal semantics: room.quantity = number of physical rooms of this
 * type, items[].quantity = count per room (already per-room, not summed).
 * So one proposal room with quantity = 8 expands into 8 matrix rows, each
 * carrying the items as-is.
 *
 * Naming: when quantity is 1, the room_number / room_name is the proposal
 * name verbatim (e.g. "Antigua Suite"). When quantity > 1, rows are
 * suffixed numerically (e.g. "Ocean Suite 1", "Ocean Suite 2", ...).
 * The operator can rename in the grid before saving.
 */
export function suggestFromProposal(proposalData: ProposalData | null | undefined): SuggestedRow[] {
  const rooms = proposalData?.scope?.rooms;
  // Flat-list proposals (most of them) carry scope.items and no rooms. Offer
  // them as one Totals row, same as the service-log suggestion. Onboarding
  // does the same via supabase/functions/_shared/pos/proposal-rooms.ts.
  if (!Array.isArray(rooms) || rooms.length === 0) {
    return suggestFromServiceLog(proposalData?.scope?.items);
  }

  const out: SuggestedRow[] = [];
  for (const room of rooms) {
    const baseName = (room.name || '').trim() || 'Room';
    const qty = Math.max(1, Math.floor(room.quantity ?? 1));

    const items: Record<string, number> = {};
    if (Array.isArray(room.items)) {
      for (const it of room.items) {
        const key = (it.item_name || '').trim();
        const n = Math.floor(it.quantity ?? 0);
        if (key && n > 0) items[key] = (items[key] || 0) + n;
      }
    }

    for (let i = 0; i < qty; i++) {
      const label = qty === 1 ? baseName : `${baseName} ${i + 1}`;
      out.push({
        room_number: label,
        room_name: label,
        items: { ...items },
      });
    }
  }
  return out;
}

/**
 * service_log_items row as queried by RoomsTab. Only the fields the
 * suggestion engine cares about; ignore display_order, service_date,
 * etc which do not affect the matrix. invoice_items rows share the
 * exact same shape so the same engine handles both.
 */
export interface ServiceLogItem {
  section_label?: string | null;
  section_quantity?: number | null;
  item_name?: string | null;
  quantity?: number | null;
}

/**
 * Catalogue spelling drift: legacy invoice line items often carry
 * singular nouns or omit the space before the colon ("Bed: Large",
 * "Pillow: Small"), while the canonical matrix keys are plural and
 * irregularly spaced ("Beds: Large", "Pillows : Large"). Map known
 * variants to the canonical names so the bed-count derivation and
 * the matrix columns line up regardless of source.
 *
 * Anything not in this map flows through unchanged.
 */
const ITEM_NAME_VARIANTS: Record<string, string> = {
  'bed: large': 'Beds: Large',
  'bed: small': 'Beds: Small',
  'beds: large': 'Beds: Large',
  'beds: small': 'Beds: Small',
  'pillow: large': 'Pillows : Large',
  'pillow: small': 'Pillows : Small',
  'pillows: large': 'Pillows : Large',
  'pillows: small': 'Pillows : Small',
  'topper: large': 'Topper : Large',
  'topper: small': 'Topper : Small',
  'electric blanket: large': 'Electric Blankets : Large',
  'electric blanket: small': 'Electric Blankets : Small',
  'electric blankets: large': 'Electric Blankets : Large',
  'electric blankets: small': 'Electric Blankets : Small',
};

function canonicaliseItemName(raw: string): string {
  const key = raw.trim().toLowerCase();
  return ITEM_NAME_VARIANTS[key] ?? raw.trim();
}

/**
 * Canonical room-inventory item keys, derived from the Rooms matrix
 * grid columns. Anything outside this set is a charge or a non-room
 * line item (Travel, Toll Gate, Accommodation, etc) and should not
 * leak into a suggested row.
 */
const CANONICAL_ROOM_ITEMS: ReadonlySet<string> = new Set([
  'Beds: Large', 'Beds: Small',
  'Pillows : Large', 'Pillows : Small',
  'Topper : Large', 'Topper : Small',
  'Electric Blankets : Large', 'Electric Blankets : Small',
  'Headboard - Large', 'Headboard: Medium', 'Headboard: Small',
  'Ottoman: Large', 'Ottoman: Small',
  'Sofa: 1 Seater', 'Sofa: 2 Seater', 'Sofa: 3 Seater',
  'Sofa: 4 Seater', 'Sofa: 5 Seater', 'Sofa: 6 Seater',
  'Cots', 'Chair: Fabric / Upholstered',
]);

/**
 * Build matrix rows from a list of line items (service_log_items or
 * invoice_items - same shape).
 *
 * Always produces a SINGLE "Totals" row. The operator can split it
 * per-room afterwards in the Rooms tab. The previous section-aware
 * splitting produced surprising shapes when source data had inconsistent
 * section_label values and dragged in non-room items like Travel and
 * Toll Gate.
 *
 * Items are:
 *   - canonicalised (Bed: Large -> Beds: Large etc),
 *   - filtered to canonical room inventory only (charges dropped),
 *   - summed across duplicate entries.
 *
 * Returns an empty array if no canonical room item has a positive
 * quantity, signalling to the caller it should fall back to another
 * source (e.g. property.total_beds).
 */
export function suggestFromServiceLog(items: ServiceLogItem[] | null | undefined): SuggestedRow[] {
  if (!Array.isArray(items) || items.length === 0) return [];

  const merged: Record<string, number> = {};
  for (const it of items) {
    const rawName = (it.item_name ?? '').trim();
    if (!rawName) continue;
    const itemName = canonicaliseItemName(rawName);
    if (!CANONICAL_ROOM_ITEMS.has(itemName)) continue;
    const qty = Math.floor(it.quantity ?? 0);
    if (qty <= 0) continue;
    merged[itemName] = (merged[itemName] || 0) + qty;
  }

  if (Object.keys(merged).length === 0) return [];

  return [{
    room_number: 'Totals',
    room_name: 'Totals',
    items: merged,
  }];
}
